'use client';
import { createContext, ReactNode, Dispatch } from 'react';

import { CACHE_TIME } from '@/const/cache';
import { Closet } from '@/types/Closet';

import { noop } from '@/util/misc';

import { useContextBroker } from '../ContextBroker/useContextBroker';
import { BrokerAction } from '../ContextBroker/ContextBroker';

export type ClosetContextValue = {
  closet: Closet | null;
};

export type ClosetBrokerActions = {
  refreshCloset: () => Promise<ClosetContextValue>;
};

export type ClosetBrokeredValue = {
  value: ClosetContextValue;
  actions: ClosetBrokerActions;
};

export const INITIAL_CLOSET_VALUE = {
  closet: null
};

export const ClosetContext = createContext<ClosetBrokeredValue>({
  value: INITIAL_CLOSET_VALUE,
  actions: {
    refreshCloset: noop
  }
});

type ClosetProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function ClosetProvider(props: ClosetProviderProps) {
  const { children, dispatch } = props;

  const brokerValue = useContextBroker();

  const decoratedValue: ClosetBrokeredValue = {
    value: brokerValue.closet,

    actions: {
      refreshCloset: async (): Promise<ClosetContextValue> => {
        /*
          NOTE: This whole logic needs to be mirrored in ClosetContext.hydrator
          since we need separate code running on server and client
          This is client side code
        */
        const response = await fetch(`/api/closet`, {
          method: 'GET',
          headers: {
            'content-type': 'application/json'
          },
          next: {
            revalidate: CACHE_TIME.NO_CACHE
          }
        });

        const closetContextValue: ClosetContextValue = { closet: null };

        const json = (await response.json()) as {
          success: boolean;
          data: Closet;
        };

        const closet = json.data;
        if (closet) {
          closetContextValue.closet = closet;
        }

        dispatch({
          type: 'CLOSET_REFRESH',
          newContextValue: closetContextValue
        });

        return closetContextValue;
      }
    }
  };

  return (
    <ClosetContext.Provider value={decoratedValue}>
      {children}
    </ClosetContext.Provider>
  );
}

export default ClosetProvider;
